/**
 * QRCodeEditorPage
 *
 * Full-page editor for creating and editing QR codes.
 * Replaces the modal-based CreateQRCodeModal with a routable page.
 *
 * Routes:
 * - /qr-codes/new - Create new QR code
 * - /qr-codes/:id/edit - Edit existing QR code
 *
 * Steps:
 * 1. Basics - Name, target URL, description, project
 * 2. Customize - Colors, shapes, logo options
 * 3. Publish - Summary with live preview and download
 */

import { useEffect, useCallback, useState, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  QrCode,
  Palette,
  CheckCircle2,
  Crown,
  Upload,
  RefreshCw,
  Image,
  Square,
  Circle,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { FullPageEditorLayout } from '@/client/components/editors';
import { WizardStep, type WizardStepConfig } from '@/client/components/wizard';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Button } from '@/client/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { useFeatureGate, TierBadge } from '@/client/components/feature-gate';

// ⚡ Performance Optimization: Lazy load QR code library
const LazyQRCodeCanvas = lazy(() =>
  import('qrcode.react').then((module) => ({
    default: module.QRCodeCanvas,
  }))
);

// =============================================================================
// Types
// =============================================================================

type BodyShape = 'square' | 'rounded' | 'circle';
type EyeFrameShape = 'square' | 'rounded';
type EyeBallShape = 'square' | 'rounded' | 'circle';

interface ProjectOption {
  id: string;
  name: string;
  color: string;
  isDefault?: boolean;
}

interface QRCodeResponse {
  id: string;
  name: string;
  description?: string | null;
  targetUrl: string;
  projectId: string;
  styleOptions?: {
    size?: number;
    margin?: number;
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
    darkColor?: string;
    lightColor?: string;
    bodyShape?: BodyShape;
    eyeFrameShape?: EyeFrameShape;
    eyeBallShape?: EyeBallShape;
  };
  logoOptions?: {
    type: 'default' | 'custom' | 'none';
    customUrl?: string;
    size?: number;
  };
}

// =============================================================================
// Schema
// =============================================================================

const qrCodeEditorSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  targetUrl: z.string().url('Please enter a valid URL').max(2048),
  description: z.string().max(500).optional(),
  projectId: z.string().optional(),
  // Style options
  fgColor: z.string(),
  bgColor: z.string(),
  bodyShape: z.enum(['square', 'rounded', 'circle']),
  eyeFrameShape: z.enum(['square', 'rounded']),
  eyeBallShape: z.enum(['square', 'rounded', 'circle']),
  // Logo options
  logoType: z.enum(['default', 'custom', 'none']),
  customLogoUrl: z.string().optional(),
});

type QRCodeEditorInput = z.infer<typeof qrCodeEditorSchema>;

// =============================================================================
// Color Presets
// =============================================================================

const COLOR_PRESETS = [
  { name: 'Classic', fg: '#000000', bg: '#ffffff' },
  { name: 'Indigo', fg: '#4f46e5', bg: '#eef2ff' },
  { name: 'Ocean', fg: '#0891b2', bg: '#ecfeff' },
  { name: 'Forest', fg: '#166534', bg: '#f0fdf4' },
  { name: 'Sunset', fg: '#ea580c', bg: '#fff7ed' },
  { name: 'Night', fg: '#f8fafc', bg: '#0f172a' },
];

// =============================================================================
// Wizard Steps Configuration
// =============================================================================

const getWizardSteps = (): WizardStepConfig[] => [
  { id: 'basics', label: 'Basics', icon: QrCode },
  { id: 'customize', label: 'Customize', icon: Palette, optional: true },
  { id: 'publish', label: 'Publish', icon: CheckCircle2 },
];

// =============================================================================
// Step Components
// =============================================================================

interface BasicsStepProps {
  projects: ProjectOption[];
}

function BasicsStep({ projects }: BasicsStepProps) {
  const form = useFormContext<QRCodeEditorInput>();

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          type="text"
          id="name"
          autoComplete="off"
          spellCheck={true}
          {...form.register('name')}
          placeholder="e.g., Product Launch QR"
          autoFocus
        />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
        )}
        <p className="text-xs text-muted-foreground">A friendly name to identify this QR code</p>
      </div>

      {/* Target URL */}
      <div className="space-y-2">
        <Label htmlFor="targetUrl">
          Target URL <span className="text-destructive">*</span>
        </Label>
        <Input
          type="url"
          id="targetUrl"
          autoComplete="url"
          inputMode="url"
          spellCheck={false}
          {...form.register('targetUrl')}
          className="font-mono text-sm"
          placeholder="https://example.com/page"
        />
        {form.formState.errors.targetUrl && (
          <p className="text-sm text-destructive">{form.formState.errors.targetUrl.message}</p>
        )}
        <p className="text-xs text-muted-foreground">Where should the QR code redirect to?</p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">
          Description <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <textarea
          id="description"
          {...form.register('description')}
          placeholder="Optional description"
          rows={2}
          className="flex min-h-[60px] w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
        />
      </div>

      {/* Project Selection */}
      {projects.length > 0 && (
        <div className="space-y-2">
          <Label>Project</Label>
          <Select
            value={form.watch('projectId') || '__none__'}
            onValueChange={(value) =>
              form.setValue('projectId', value === '__none__' ? undefined : value)
            }
          >
            <SelectTrigger className="w-full max-w-xs rounded-xl">
              <SelectValue placeholder="Default Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Default Project</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color || '#6366f1' }}
                    />
                    {project.name}
                    {project.isDefault ? ' (Default)' : ''}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function CustomizeStep() {
  const form = useFormContext<QRCodeEditorInput>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const fgColor = form.watch('fgColor');
  const bgColor = form.watch('bgColor');
  const bodyShape = form.watch('bodyShape');
  const eyeFrameShape = form.watch('eyeFrameShape');
  const eyeBallShape = form.watch('eyeBallShape');
  const logoType = form.watch('logoType');
  const customLogoUrl = form.watch('customLogoUrl');

  // Feature gates
  const {
    hasAccess: hasBrandedQR,
    requiredTier: brandedQRTier,
    openUpgradeModal: openBrandedQRModal,
    UpgradeModalElement: BrandedQRUpgradeModal,
  } = useFeatureGate('branded_qr_codes', 'Customize QR code shapes and remove the watermark');

  const {
    hasAccess: hasCustomLogo,
    requiredTier: customLogoTier,
    openUpgradeModal: openCustomLogoModal,
    UpgradeModalElement: CustomLogoUpgradeModal,
  } = useFeatureGate('qr_custom_logo', 'Upload your own logo to QR codes');

  // Handle logo file upload
  const handleLogoUpload = async (file: File) => {
    if (!hasCustomLogo) {
      openCustomLogoModal();
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Logo must be PNG, JPEG, or SVG');
      return;
    }

    if (file.size > 500 * 1024) {
      toast.error('Logo must be smaller than 500KB');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await apiFetch('/api/qr-codes/upload-logo', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload logo');
      }

      const data = await response.json();
      form.setValue('customLogoUrl', data.logoUrl);
      form.setValue('logoType', 'custom');
      toast.success('Logo uploaded successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Color Presets */}
      <div className="space-y-3">
        <Label>Color Presets</Label>
        <div className="grid grid-cols-3 gap-2">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => {
                form.setValue('fgColor', preset.fg);
                form.setValue('bgColor', preset.bg);
              }}
              className={cn(
                'p-2.5 rounded-xl border-2 text-xs font-medium transition-all',
                fgColor === preset.fg && bgColor === preset.bg
                  ? 'border-indigo-500 ring-2 ring-indigo-500/20'
                  : 'border-transparent bg-muted/50 hover:bg-muted'
              )}
              style={{ backgroundColor: preset.bg, color: preset.fg }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Foreground</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={fgColor}
              onChange={(e) => form.setValue('fgColor', e.target.value)}
              className="w-10 h-10 rounded-lg cursor-pointer border"
            />
            <Input
              value={fgColor}
              onChange={(e) => form.setValue('fgColor', e.target.value)}
              className="flex-1 font-mono text-xs uppercase"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Background</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={bgColor}
              onChange={(e) => form.setValue('bgColor', e.target.value)}
              className="w-10 h-10 rounded-lg cursor-pointer border"
            />
            <Input
              value={bgColor}
              onChange={(e) => form.setValue('bgColor', e.target.value)}
              className="flex-1 font-mono text-xs uppercase"
            />
          </div>
        </div>
      </div>

      {/* Shape Options */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label>Shape Options</Label>
          {!hasBrandedQR && <TierBadge tier={brandedQRTier} size="xs" />}
        </div>

        {hasBrandedQR ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Body Shape</Label>
              <div className="flex gap-2">
                {(['square', 'rounded', 'circle'] as BodyShape[]).map((shape) => (
                  <button
                    type="button"
                    key={shape}
                    onClick={() => form.setValue('bodyShape', shape)}
                    className={cn(
                      'flex-1 p-2 rounded-lg border-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-all',
                      bodyShape === shape
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-600'
                        : 'border-muted hover:border-muted-foreground/20'
                    )}
                  >
                    {shape === 'square' && <Square className="size-3" />}
                    {shape === 'rounded' && <Square className="size-3 rounded-sm" />}
                    {shape === 'circle' && <Circle className="size-3" />}
                    <span className="capitalize">{shape}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Eye Frame</Label>
              <div className="flex gap-2">
                {(['square', 'rounded'] as EyeFrameShape[]).map((shape) => (
                  <button
                    type="button"
                    key={shape}
                    onClick={() => form.setValue('eyeFrameShape', shape)}
                    className={cn(
                      'flex-1 p-2 rounded-lg border-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-all',
                      eyeFrameShape === shape
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-600'
                        : 'border-muted hover:border-muted-foreground/20'
                    )}
                  >
                    {shape === 'square' && <Square className="size-3" />}
                    {shape === 'rounded' && <Square className="size-3 rounded-sm" />}
                    <span className="capitalize">{shape}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Eye Ball</Label>
              <div className="flex gap-2">
                {(['square', 'rounded', 'circle'] as EyeBallShape[]).map((shape) => (
                  <button
                    type="button"
                    key={shape}
                    onClick={() => form.setValue('eyeBallShape', shape)}
                    className={cn(
                      'flex-1 p-2 rounded-lg border-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-all',
                      eyeBallShape === shape
                        ? 'border-indigo-500 bg-indigo-500/10 text-indigo-600'
                        : 'border-muted hover:border-muted-foreground/20'
                    )}
                  >
                    {shape === 'square' && <Square className="size-3" />}
                    {shape === 'rounded' && <Square className="size-3 rounded-sm" />}
                    {shape === 'circle' && <Circle className="size-3" />}
                    <span className="capitalize">{shape}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={openBrandedQRModal}
            className="w-full p-3 rounded-xl bg-gradient-to-r from-indigo-500/5 to-cyan-500/5 border border-indigo-500/10 hover:border-indigo-500/30 transition-colors cursor-pointer text-left"
          >
            <p className="text-xs text-muted-foreground">
              Customize body shapes, eye frames, and eye balls.
            </p>
            <span className="text-xs font-medium text-indigo-600 flex items-center gap-1 mt-1">
              <Crown className="size-2.5" /> Upgrade to unlock
            </span>
          </button>
        )}
      </div>

      {/* Logo Options */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label>Logo</Label>
          {!hasBrandedQR && <TierBadge tier={brandedQRTier} size="xs" />}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => form.setValue('logoType', 'default')}
            className={cn(
              'flex-1 p-2.5 rounded-xl border-2 text-xs font-medium transition-all',
              logoType === 'default'
                ? 'border-indigo-500 bg-indigo-500/5'
                : 'border-muted hover:border-muted-foreground/20'
            )}
          >
            Armenian Link
          </button>
          {hasBrandedQR ? (
            <button
              type="button"
              onClick={() => form.setValue('logoType', 'none')}
              className={cn(
                'flex-1 p-2.5 rounded-xl border-2 text-xs font-medium transition-all',
                logoType === 'none'
                  ? 'border-indigo-500 bg-indigo-500/5'
                  : 'border-muted hover:border-muted-foreground/20'
              )}
            >
              No Logo
            </button>
          ) : (
            <button
              type="button"
              onClick={openBrandedQRModal}
              className="flex-1 p-2.5 rounded-xl border-2 border-dashed text-xs font-medium transition-all border-muted hover:border-amber-500/30 flex items-center justify-center gap-1 text-muted-foreground"
            >
              <Crown className="size-3 text-amber-500" />
              No Logo
            </button>
          )}
          {hasCustomLogo ? (
            <button
              type="button"
              onClick={() => form.setValue('logoType', 'custom')}
              className={cn(
                'flex-1 p-2.5 rounded-xl border-2 text-xs font-medium transition-all flex items-center justify-center gap-1',
                logoType === 'custom'
                  ? 'border-indigo-500 bg-indigo-500/5'
                  : 'border-muted hover:border-muted-foreground/20'
              )}
            >
              <Image className="size-3" />
              Custom
            </button>
          ) : (
            <button
              type="button"
              onClick={openCustomLogoModal}
              className="flex-1 p-2.5 rounded-xl border-2 border-dashed text-xs font-medium transition-all border-muted hover:border-violet-500/30 flex items-center justify-center gap-1 text-muted-foreground"
            >
              <Image className="size-3" />
              Custom
              <TierBadge tier={customLogoTier} size="xs" />
            </button>
          )}
        </div>

        {/* Custom Logo Upload */}
        {logoType === 'custom' && hasCustomLogo && (
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={handleFileSelect}
              className="hidden"
            />
            {customLogoUrl ? (
              <div className="flex items-center gap-3 p-3 rounded-xl border bg-muted/30">
                <img
                  src={customLogoUrl}
                  alt="Custom logo"
                  className="size-10 rounded-lg object-cover bg-white"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">Custom Logo</p>
                  <p className="text-xs text-muted-foreground">Uploaded successfully</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingLogo}
                >
                  <RefreshCw className={cn('size-3.5', isUploadingLogo && 'animate-spin')} />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingLogo}
                className="w-full p-4 rounded-xl border-2 border-dashed border-muted hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all flex flex-col items-center gap-2"
              >
                {isUploadingLogo ? (
                  <>
                    <RefreshCw className="size-5 text-muted-foreground animate-spin" />
                    <span className="text-xs text-muted-foreground">Uploading…</span>
                  </>
                ) : (
                  <>
                    <Upload className="size-5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Click to upload (PNG, JPEG, SVG · Max 500KB)
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {BrandedQRUpgradeModal}
        {CustomLogoUpgradeModal}
      </div>
    </div>
  );
}

function PublishStep({ isEditMode }: { isEditMode: boolean }) {
  const form = useFormContext<QRCodeEditorInput>();
  const name = form.watch('name');
  const targetUrl = form.watch('targetUrl');
  const description = form.watch('description');

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-6 rounded-xl border bg-card">
        <h3 className="font-medium mb-4">Ready to {isEditMode ? 'Save' : 'Create'}</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Name</dt>
            <dd className="font-medium truncate max-w-[200px]">{name || '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Target URL</dt>
            <dd className="font-mono text-xs truncate max-w-[200px]" title={targetUrl}>
              {targetUrl || '—'}
            </dd>
          </div>
          {description && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Description</dt>
              <dd className="font-medium truncate max-w-[200px]">{description}</dd>
            </div>
          )}
        </dl>
      </div>
      <p className="text-sm text-muted-foreground">
        {isEditMode
          ? 'Click "Save" to update your QR code.'
          : 'Click "Create" to generate your QR code. You can download it immediately after.'}
      </p>
    </div>
  );
}

// =============================================================================
// Preview Component
// =============================================================================

function QRCodePreview() {
  const form = useFormContext<QRCodeEditorInput>();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const targetUrl = form.watch('targetUrl');
  const fgColor = form.watch('fgColor');
  const bgColor = form.watch('bgColor');
  const logoType = form.watch('logoType');
  const customLogoUrl = form.watch('customLogoUrl');
  const bodyShape = form.watch('bodyShape');
  const eyeFrameShape = form.watch('eyeFrameShape');
  const eyeBallShape = form.watch('eyeBallShape');

  const showLogo = logoType !== 'none';
  const logoSrc = logoType === 'custom' && customLogoUrl ? customLogoUrl : '/assets/logo.png';

  const hasCustomShapes =
    bodyShape !== 'square' || eyeFrameShape !== 'square' || eyeBallShape !== 'square';

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('QR code not ready');
      return;
    }

    const link = document.createElement('a');
    link.download = 'qr-code.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-xs text-muted-foreground font-medium">Live Preview</p>
      <Suspense
        fallback={<div className="w-[180px] h-[180px] bg-muted animate-pulse rounded-xl" />}
      >
        <div className="p-5 rounded-xl border bg-white shadow-lg">
          <LazyQRCodeCanvas
            ref={canvasRef as React.RefObject<HTMLCanvasElement>}
            value={targetUrl || 'https://armenian.link'}
            size={180}
            level={showLogo ? 'H' : 'M'}
            marginSize={1}
            fgColor={fgColor}
            bgColor={bgColor}
            imageSettings={
              showLogo
                ? {
                    src: logoSrc,
                    height: 36,
                    width: 36,
                    excavate: true,
                  }
                : undefined
            }
          />
        </div>
      </Suspense>
      <p className="text-xs text-muted-foreground text-center max-w-[200px]">
        {hasCustomShapes ? 'Shape styles applied on save' : 'Scan to test'}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDownload}
        className="gap-2"
        disabled={!targetUrl}
      >
        <Download className="size-3.5" />
        Download Preview
      </Button>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function QRCodeEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEditMode = !!id;

  // Fetch projects
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  // Fetch existing QR code for edit mode
  const { data: existingQR, isLoading: isLoadingQR } = useQuery<QRCodeResponse>({
    queryKey: ['qr-codes', id],
    queryFn: async () => {
      const response = await apiFetch(`/api/qr-codes/${id}`);
      if (!response.ok) throw new Error('Failed to fetch QR code');
      return response.json();
    },
    enabled: isEditMode,
  });

  // Form setup
  const form = useForm<QRCodeEditorInput>({
    resolver: zodResolver(qrCodeEditorSchema),
    defaultValues: {
      name: '',
      targetUrl: '',
      description: '',
      projectId: undefined,
      fgColor: '#000000',
      bgColor: '#ffffff',
      bodyShape: 'square',
      eyeFrameShape: 'square',
      eyeBallShape: 'square',
      logoType: 'default',
      customLogoUrl: undefined,
    },
  });

  // Load existing QR code data
  useEffect(() => {
    if (existingQR && isEditMode) {
      form.reset({
        name: existingQR.name,
        targetUrl: existingQR.targetUrl,
        description: existingQR.description || '',
        projectId: existingQR.projectId || undefined,
        fgColor: existingQR.styleOptions?.darkColor || '#000000',
        bgColor: existingQR.styleOptions?.lightColor || '#ffffff',
        bodyShape: existingQR.styleOptions?.bodyShape || 'square',
        eyeFrameShape: existingQR.styleOptions?.eyeFrameShape || 'square',
        eyeBallShape: existingQR.styleOptions?.eyeBallShape || 'square',
        logoType: existingQR.logoOptions?.type || 'default',
        customLogoUrl: existingQR.logoOptions?.customUrl || undefined,
      });
    }
  }, [existingQR, isEditMode, form]);

  const isDirty = form.formState.isDirty;

  // Feature gates for access checks
  const { hasAccess: hasBrandedQR } = useFeatureGate('branded_qr_codes');
  const { hasAccess: hasCustomLogo } = useFeatureGate('qr_custom_logo');

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: QRCodeEditorInput) => {
      const styleOptions: Record<string, unknown> = {
        darkColor: data.fgColor,
        lightColor: data.bgColor,
      };

      if (hasBrandedQR) {
        styleOptions.bodyShape = data.bodyShape;
        styleOptions.eyeFrameShape = data.eyeFrameShape;
        styleOptions.eyeBallShape = data.eyeBallShape;
      }

      const logoOptions: Record<string, unknown> = {
        type: data.logoType,
      };

      if (data.logoType === 'custom' && data.customLogoUrl && hasCustomLogo) {
        logoOptions.customUrl = data.customLogoUrl;
      }

      const payload = {
        name: data.name,
        targetUrl: data.targetUrl,
        description: data.description || undefined,
        projectId: data.projectId || undefined,
        styleOptions,
        logoOptions,
      };

      const response = await apiFetch('/api/qr-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          typeof error.error === 'string'
            ? error.error
            : error.message || 'Failed to create QR code'
        );
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast.success('QR code created!');
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      navigate(`/qr-codes/${data.id}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: QRCodeEditorInput) => {
      const styleOptions: Record<string, unknown> = {
        darkColor: data.fgColor,
        lightColor: data.bgColor,
      };

      if (hasBrandedQR) {
        styleOptions.bodyShape = data.bodyShape;
        styleOptions.eyeFrameShape = data.eyeFrameShape;
        styleOptions.eyeBallShape = data.eyeBallShape;
      }

      const logoOptions: Record<string, unknown> = {
        type: data.logoType,
      };

      if (data.logoType === 'custom' && data.customLogoUrl && hasCustomLogo) {
        logoOptions.customUrl = data.customLogoUrl;
      }

      const payload = {
        name: data.name,
        targetUrl: data.targetUrl,
        description: data.description || undefined,
        projectId: data.projectId || undefined,
        styleOptions,
        logoOptions,
      };

      const response = await apiFetch(`/api/qr-codes/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          typeof error.error === 'string'
            ? error.error
            : error.message || 'Failed to update QR code'
        );
      }

      return response.json();
    },
    onSuccess: () => {
      toast.success('QR code updated!');
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      queryClient.invalidateQueries({ queryKey: ['qr-codes', id] });
      navigate('/qr-codes');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Handle complete
  const handleComplete = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error('Please fix the errors before saving');
      return;
    }

    const data = form.getValues();

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  }, [form, isEditMode, updateMutation, createMutation]);

  // Step validation
  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      const step = wizardSteps[stepIndex];
      if (!step) return true;

      if (step.id === 'basics') {
        const result = await form.trigger(['name', 'targetUrl']);
        if (!result) {
          toast.error('Please fill in the required fields');
        }
        return result;
      }

      return true;
    },
    [form]
  );

  const wizardSteps = getWizardSteps();
  const isLoading = isEditMode && (isLoadingQR || isLoadingProjects);

  return (
    <FormProvider {...form}>
      <FullPageEditorLayout
        title={isEditMode ? 'Edit QR Code' : 'Create QR Code'}
        subtitle={
          isEditMode
            ? `Editing ${existingQR?.name || 'QR code'}`
            : 'Generate a scannable code for any URL'
        }
        steps={wizardSteps}
        initialStep={0}
        isEditMode={isEditMode}
        isLoading={isLoading}
        isDirty={isDirty}
        onComplete={handleComplete}
        completeButtonText={isEditMode ? 'Save' : 'Create'}
        accentColor="violet"
        backPath="/qr-codes"
        hasPreview={false}
        validateStep={validateStep}
      >
        <WizardStep stepId="basics">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <BasicsStep projects={projects} />
            </div>
            <div className="lg:w-64 flex-shrink-0">
              <QRCodePreview />
            </div>
          </div>
        </WizardStep>

        <WizardStep stepId="customize">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <CustomizeStep />
            </div>
            <div className="lg:w-64 flex-shrink-0">
              <QRCodePreview />
            </div>
          </div>
        </WizardStep>

        <WizardStep stepId="publish">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <PublishStep isEditMode={isEditMode} />
            </div>
            <div className="lg:w-64 flex-shrink-0">
              <QRCodePreview />
            </div>
          </div>
        </WizardStep>
      </FullPageEditorLayout>
    </FormProvider>
  );
}
